import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SearchableCombobox from "@/components/SearchableCombobox";
import { fmtDateTime } from "./commMeta";

const cname = (c) => c.display_name || c.company_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.id;
const today = () => new Date().toISOString().slice(0, 10);

// Eingehendes Schreiben / E-Mail erfassen (Upload PDF/Scan/.eml oder Text), optional als Antwort zuordnen
export default function IncomingDialog({ open, onOpenChange, scope, inReplyTo, onSaved }) {
  const [f, setF] = useState({});
  const [files, setFiles] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    if (!open) return;
    setFiles([]);
    setF({ kind: "letter", subject: "", text: "", occurred_at: today(), sender_contact_id: inReplyTo?.recipients?.[0]?.contact_id || scope.contact_id || "",
      in_reply_to_id: inReplyTo?.id || "", follow_up_date: "" });
    api.get("/contacts", { params: { limit: 1000 } }).then(({ data }) => setContacts(data || []));
    api.get("/communications", { params: { ...scope, direction: "out", limit: 50 } }).then(({ data }) => setOutgoing(data.items || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const save = async () => {
    const fd = new FormData();
    Object.entries({ ...f, ...scope }).forEach(([k, v]) => { if (v && k !== "contact_id") fd.append(k, v); });
    if (f.occurred_at) fd.set("occurred_at", new Date(`${f.occurred_at}T12:00:00`).toISOString());
    files.forEach((x) => fd.append("files", x));
    setBusy(true);
    try {
      const { data } = await api.post("/communications/incoming", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(f.in_reply_to_id ? "Eingang erfasst und als Antwort zugeordnet" : "Eingang erfasst");
      onOpenChange(false);
      onSaved?.(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erfassen fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="comm-incoming-dialog"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files?.length) setFiles((x) => [...x, ...e.dataTransfer.files]); }}>
        <DialogHeader>
          <DialogTitle>Eingang erfassen</DialogTitle>
          <DialogDescription>Brief-Scan, PDF oder E-Mail hochladen – Outlook-Mails einfach hierher ziehen (.msg/.eml). Betreff, Absender, Datum und Anhänge werden übernommen.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Art</Label>
            <Select value={f.kind || "letter"} onValueChange={(v) => set("kind", v)}>
              <SelectTrigger data-testid="comm-in-kind"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="letter">Brief</SelectItem><SelectItem value="email">E-Mail</SelectItem></SelectContent>
            </Select></div>
          <div><Label>Eingangsdatum</Label>
            <Input type="date" value={f.occurred_at || ""} onChange={(e) => set("occurred_at", e.target.value)} data-testid="comm-in-date" /></div>
          <div className="col-span-2"><Label>Absender</Label>
            <SearchableCombobox value={f.sender_contact_id || ""} onChange={(v) => set("sender_contact_id", v)} testId="comm-in-sender" placeholder="Kontakt wählen…"
              options={contacts.map((c) => ({ id: c.id, label: cname(c), keywords: c.owner_number || "" }))} /></div>
          <div className="col-span-2"><Label>Betreff</Label>
            <Input value={f.subject || ""} onChange={(e) => set("subject", e.target.value)} placeholder="bei .eml automatisch" data-testid="comm-in-subject" /></div>
          <div className="col-span-2"><Label>Antwort auf (optional)</Label>
            <Select value={f.in_reply_to_id || "none"} onValueChange={(v) => set("in_reply_to_id", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="comm-in-reply-to"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— keine Zuordnung —</SelectItem>
                {inReplyTo && !outgoing.some((o) => o.id === inReplyTo.id) && <SelectItem value={inReplyTo.id}>{inReplyTo.subject}</SelectItem>}
                {outgoing.map((o) => <SelectItem key={o.id} value={o.id}>{fmtDateTime(o.occurred_at).slice(0, 10)} · {o.subject}{o.follow_up?.status === "open" ? " (Wiedervorlage offen)" : ""}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div className="col-span-2"><Label>Dateien</Label>
            <Input type="file" multiple accept=".pdf,.eml,image/*,.msg,.txt,.docx" onChange={(e) => setFiles([...e.target.files])} data-testid="comm-in-files" />
            {files.length > 0 && <div className="text-[11px] text-slate-500 mt-1">{files.map((x) => x.name).join(", ")}</div>}</div>
          <div className="col-span-2"><Label>Text / Inhalt (optional)</Label>
            <Textarea rows={4} value={f.text || ""} onChange={(e) => set("text", e.target.value)} placeholder="z. B. eingefügter E-Mail-Text" data-testid="comm-in-text" /></div>
          <div><Label>Wiedervorlage (optional)</Label>
            <Input type="date" value={f.follow_up_date || ""} onChange={(e) => set("follow_up_date", e.target.value)} data-testid="comm-in-followup" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={save} disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid="comm-in-save">{busy ? "Speichere…" : "Erfassen"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
